import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faComments,
  faDatabase,
  faEllipsis,
  faEye,
  faGlobe,
  faPen,
  faPlus,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api, { getData } from "../services/api";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/format";
import {
  Button,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  PageHeader,
  Pagination,
  Skeleton,
  StatusBadge,
} from "../components/UI";

const blank = {
  name: "",
  domain: "",
  description: "",
  allowedDomains: "",
  status: "active",
  chatbotEnabled: true,
  allowGeneralKnowledge: false,
};
export default function WebsitesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [query, setQuery] = useState({
    search: "",
    status: "",
    sortBy: "updatedAt",
    sortOrder: "desc",
    page: 1,
    limit: 10,
  });
  const [state, setState] = useState({
    loading: true,
    items: [],
    pagination: {},
    error: "",
  });
  const [modal, setModal] = useState(
    location.state?.add ? { mode: "add", data: blank } : null,
  );
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState({});
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    api
      .get("/websites", { params: query })
      .then((r) => {
        const data = getData(r);
        setState({
          loading: false,
          items: data.items,
          pagination: data.pagination,
          error: "",
        });
      })
      .catch((e) =>
        setState({
          loading: false,
          items: [],
          pagination: {},
          error: e.message,
        }),
      );
  }, [query]);
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...modal.data,
        allowedDomains: modal.data.allowedDomains
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      };
      if (modal.mode === "add") await api.post("/websites", body);
      else await api.patch(`/websites/${modal.data._id}`, body);
      toast.show(`Website ${modal.mode === "add" ? "created" : "updated"}.`);
      setModal(null);
      load();
    } catch (err) {
      toast.show(err.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (site, key) => {
    const toggleId = `${site._id}:${key}`;
    const nextValue =
      key === "status"
        ? site.status === "active"
          ? "inactive"
          : "active"
        : !Boolean(site[key]);

    setToggling((current) => ({ ...current, [toggleId]: true }));
    try {
      await api.patch(`/websites/${site._id}`, {
        [key]: nextValue,
      });
      setState((current) => ({
        ...current,
        items: current.items.map((item) =>
          item._id === site._id ? { ...item, [key]: nextValue } : item,
        ),
      }));
      toast.show(
        key === "allowGeneralKnowledge"
          ? nextValue
            ? "General answers enabled."
            : "Knowledge-only answers enabled."
          : "Website status updated.",
      );
    } catch (e) {
      toast.show(e.message, "error");
    } finally {
      setToggling((current) => {
        const next = { ...current };
        delete next[toggleId];
        return next;
      });
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/websites/${deleting._id}`);
      toast.show("Website deleted.");
      setDeleting(null);
      load();
    } catch (e) {
      if (e.status === 409 && e.details?.confirmation) {
        try {
          await api.delete(`/websites/${deleting._id}`, {
            params: { confirm: e.details.confirmation },
          });
          toast.show("Website and its associated data were deleted.");
          setDeleting(null);
          load();
        } catch (finalError) {
          toast.show(finalError.message, "error");
        }
      } else toast.show(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const action = (site, icon, label, path) => (
    <button
      title={label}
      aria-label={`${label} ${site.name}`}
      onClick={() => navigate(path)}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
  return (
    <>
      <PageHeader
        eyebrow="Tenant registry"
        title="Websites"
        description="Manage domains, knowledge scope, and assistant availability."
        actions={
          <Button
            icon={faPlus}
            onClick={() => setModal({ mode: "add", data: blank })}
          >
            Add website
          </Button>
        }
      />
      <section className="panel toolbar">
        <label className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            aria-label="Search websites"
            placeholder="Search websites or domains…"
            value={query.search}
            onChange={(e) =>
              setQuery({ ...query, search: e.target.value, page: 1 })
            }
          />
        </label>
        <select
          aria-label="Filter by status"
          value={query.status}
          onChange={(e) =>
            setQuery({ ...query, status: e.target.value, page: 1 })
          }
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          aria-label="Sort websites"
          value={`${query.sortBy}:${query.sortOrder}`}
          onChange={(e) => {
            const [sortBy, sortOrder] = e.target.value.split(":");
            setQuery({ ...query, sortBy, sortOrder });
          }}
        >
          <option value="updatedAt:desc">Recently updated</option>
          <option value="name:asc">Name A–Z</option>
          <option value="domain:asc">Domain A–Z</option>
        </select>
      </section>
      {state.loading ? (
        <Skeleton rows={6} />
      ) : state.error ? (
        <ErrorState message={state.error} onRetry={load} />
      ) : !state.items.length ? (
        <EmptyState
          icon={faGlobe}
          title="No websites match"
          message={
            query.search || query.status
              ? "Adjust the search or filter to widen the result."
              : "Add your first website to begin building a scoped assistant."
          }
          action={
            !query.search &&
            !query.status && (
              <Button
                icon={faPlus}
                onClick={() => setModal({ mode: "add", data: blank })}
              >
                Add first website
              </Button>
            )
          }
        />
      ) : (
        <section className="panel table-panel">
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Website</th>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Knowledge</th>
                  <th>Chatbot</th>
                  <th>General AI</th>
                  <th>Updated</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((site) => (
                  <tr key={site._id}>
                    <td data-label="Website">
                      <div className="site-cell">
                        <span>{site.name?.[0]}</span>
                        <strong>{site.name}</strong>
                      </div>
                    </td>
                    <td data-label="Domain">
                      <span className="domain-text">{site.domain}</span>
                    </td>
                    <td data-label="Status">
                      <button
                        className="status-toggle"
                        onClick={() => toggle(site, "status")}
                        aria-label={`${site.status === "active" ? "Disable" : "Enable"} ${site.name}`}
                      >
                        <StatusBadge active={site.status === "active"} />
                      </button>
                    </td>
                    <td data-label="Knowledge">
                      <strong>{site.knowledgeRecords || 0}</strong> records
                    </td>
                    <td data-label="Chatbot">
                      <button
                        className="status-toggle"
                        onClick={() => toggle(site, "chatbotEnabled")}
                        aria-label={`${site.chatbotEnabled ? "Disable" : "Enable"} chatbot for ${site.name}`}
                      >
                        <StatusBadge active={site.chatbotEnabled}>
                          {site.chatbotEnabled ? "Enabled" : "Disabled"}
                        </StatusBadge>
                      </button>
                    </td>
                    <td data-label="General AI">
                      <div className="policy-control">
                        <button
                          type="button"
                          className="policy-switch"
                          role="switch"
                          aria-checked={Boolean(site.allowGeneralKnowledge)}
                          aria-busy={Boolean(
                            toggling[
                              `${site._id}:allowGeneralKnowledge`
                            ],
                          )}
                          aria-label={`${site.allowGeneralKnowledge ? "Disable general answers for" : "Enable general answers for"} ${site.name}`}
                          title={
                            site.allowGeneralKnowledge
                              ? "Use imported knowledge first, with short general answers when needed"
                              : "Restrict answers to imported knowledge"
                          }
                          disabled={Boolean(
                            toggling[
                              `${site._id}:allowGeneralKnowledge`
                            ],
                          )}
                          onClick={() =>
                            toggle(site, "allowGeneralKnowledge")
                          }
                        >
                          <span className="policy-switch__track" aria-hidden="true">
                            <span className="policy-switch__thumb" />
                          </span>
                        </button>
                        <span className="policy-control__label">
                          {toggling[`${site._id}:allowGeneralKnowledge`]
                            ? "Saving…"
                            : site.allowGeneralKnowledge
                            ? "General answers"
                            : "Knowledge only"}
                        </span>
                      </div>
                    </td>
                    <td data-label="Updated">{formatDate(site.updatedAt)}</td>
                    <td className="row-actions">
                      <div>
                        {action(site, faEye, "View", `/websites/${site._id}`)}
                        {action(
                          site,
                          faDatabase,
                          "Knowledge",
                          `/websites/${site._id}/knowledge`,
                        )}
                        {action(
                          site,
                          faComments,
                          "Configure chatbot",
                          `/websites/${site._id}/chatbot`,
                        )}
                        {action(
                          site,
                          faChartLine,
                          "Analytics",
                          `/websites/${site._id}/analytics`,
                        )}
                        <button
                          title="Edit"
                          aria-label={`Edit ${site.name}`}
                          onClick={() =>
                            setModal({
                              mode: "edit",
                              data: {
                                ...site,
                                allowedDomains: (
                                  site.allowedDomains || []
                                ).join(", "),
                              },
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button
                          className="danger-icon"
                          title="Delete"
                          aria-label={`Delete ${site.name}`}
                          onClick={() => setDeleting(site)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      <FontAwesomeIcon
                        className="mobile-ellipsis"
                        icon={faEllipsis}
                        aria-hidden="true"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            {...state.pagination}
            onChange={(page) => setQuery({ ...query, page })}
          />
        </section>
      )}
      <Modal
        open={!!modal}
        title={modal?.mode === "add" ? "Add website" : "Edit website"}
        onClose={() => setModal(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save website"}
            </Button>
          </>
        }
      >
        <form className="form-grid" onSubmit={save}>
          {modal && (
            <>
              <Field label="Website name">
                <input
                  required
                  value={modal.data.name}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, name: e.target.value },
                    })
                  }
                />
              </Field>
              <Field
                label="Primary domain"
                hint="Enter a hostname without a path."
              >
                <input
                  required
                  placeholder="example.com"
                  value={modal.data.domain}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, domain: e.target.value },
                    })
                  }
                />
              </Field>
              <Field
                className="field-full"
                label="Allowed domains"
                hint="Comma-separated hostnames, including staging or www variants."
              >
                <input
                  placeholder="example.com, www.example.com"
                  value={modal.data.allowedDomains}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, allowedDomains: e.target.value },
                    })
                  }
                />
              </Field>
              <Field className="field-full" label="Description">
                <textarea
                  rows="4"
                  value={modal.data.description || ""}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, description: e.target.value },
                    })
                  }
                />
              </Field>
              <Field label="Website status">
                <select
                  value={modal.data.status}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, status: e.target.value },
                    })
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </Field>
              <label className="toggle-field">
                <input
                  type="checkbox"
                  checked={modal.data.chatbotEnabled}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, chatbotEnabled: e.target.checked },
                    })
                  }
                />
                <span />
                <div>
                  <strong>Chatbot enabled</strong>
                  <small>Allow the public widget on verified domains.</small>
                </div>
              </label>
              <label className="toggle-field">
                <input
                  type="checkbox"
                  checked={Boolean(modal.data.allowGeneralKnowledge)}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: {
                        ...modal.data,
                        allowGeneralKnowledge: e.target.checked,
                      },
                    })
                  }
                />
                <span />
                <div>
                  <strong>Allow general answers</strong>
                  <small>
                    Off uses imported knowledge only. On permits short general
                    answers when that knowledge does not cover the question.
                  </small>
                </div>
              </label>
            </>
          )}
        </form>
      </Modal>
      <ConfirmModal
        open={!!deleting}
        title="Delete website"
        message={`Delete ${deleting?.name}? If it has knowledge, a second domain-confirmed request will permanently remove all associated data.`}
        confirmLabel="Delete website"
        danger
        busy={busy}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}
